import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useStore } from "../store";
import { PUBLIC_ROUTES } from "../routes";
import MarketingShell from "./marketing/MarketingShell";

const VERTICALS = [
  {
    eyebrow: "Industrial",
    name: "RoboTwins",
    tone: "industrial",
    glyph: "◆",
    img: "/ascii-industrial.png",
    imgAlt: "Two industrial robot arms lifting crates over a conveyor in a moonlit factory hall, rendered in ASCII characters",
    headline: "The world twin that retasks a factory robot in a weekend.",
    body: "Our industrial foundation. RoboTwins turns a scanned work cell into a simulation environment, trains and validates robot behaviour there, and prepares it for supported UR, ABB, and FANUC connectors. The software path is real; physical-hardware validation is still pending.",
    cta: "Explore RoboTwins",
    link: PUBLIC_ROUTES.robotwins,
  },
  {
    eyebrow: "Aged Care",
    name: "Arthur",
    tone: "aged",
    glyph: "❋",
    img: "/ascii-aged-care.png",
    imgAlt: "A companion robot handing a steaming cup to an elder in an armchair by an arched moonlit window, rendered in ASCII characters",
    headline: "The mind we give a humanoid in someone&rsquo;s home.",
    body: "Most older people don&rsquo;t want a care home. Arthur is the intelligence that turns a humanoid into something safe to live alongside — helping with what hurts, catching a fall, knowing when to call the family. We bring the mind and the safety. The body can be any certified humanoid.",
    cta: "Meet Arthur",
    link: PUBLIC_ROUTES.agedCare,
  },
] as const;

const PROMISES = [
  {
    title: "It decides well — or not at all.",
    body: "Our intelligence directs the robot through the small physical jobs — fetching, lifting, repositioning — and refuses anything it isn&rsquo;t sure is safe. The judgement is the product; the hands belong to the humanoid.",
  },
  {
    title: "It knows when to step back.",
    body: "The medical decision is the GP&rsquo;s. The family decision is the family&rsquo;s. The change to a production line is the engineer&rsquo;s. The hardest engineering we do is the discipline to do nothing — and to call a human in.",
  },
  {
    title: "It&rsquo;s built to be the reason the robot gets underwritten.",
    body: "Most humanoids can&rsquo;t be deployed in care because nobody will underwrite them. Insurers want three things they rarely get: deterministic safety, an auditable record of every decision, and conformity to recognised standards. We build the evidence for all three — the reflex, the signed ledger, the standards alignment. We&rsquo;re assembling that into a safety case; we don&rsquo;t yet have a named underwriter, and we&rsquo;ll say so until we do.",
  },
  {
    title: "The house keeps its secrets.",
    body: "A care robot sees bathrooms, bedrooms, and bad days — so the raw stream dies on the robot. Perception runs locally; the cloud only ever gets signed abstractions; and the Egress Ledger itemises every byte that leaves. A family shouldn&rsquo;t have to trust. They can check.",
  },
] as const;

const PRODUCTS = [
  {
    pillar: "Pillar A",
    name: "Scan-to-Sim",
    body: "A phone walkthrough becomes a 3D world that we post-process for simulation — the map the training and validation workflow runs on.",
    link: PUBLIC_ROUTES.productScanToSim,
  },
  {
    pillar: "Pillar B",
    name: "Intelligence Core",
    body: "The planner that thinks about the whole day, not just the next step — and signs every plan before it can act.",
    link: PUBLIC_ROUTES.productIntelligenceCore,
  },
  {
    pillar: "Pillar C",
    name: "Edge Reflex",
    body: "A local safety envelope designed to refuse unsafe actions without depending on a cloud round trip. The path is tested in simulation; hardware timing is not yet proven.",
    link: PUBLIC_ROUTES.productEdgeReflex,
  },
  {
    pillar: "Pillar D",
    name: "Local Mind",
    body: "Perception designed to run on hardware you own, with the Egress Ledger itemising every byte our software does send. The local path is the target architecture; the ledger is how you hold us to it.",
    link: PUBLIC_ROUTES.productLocalMind,
  },
] as const;

export default function Landing() {
  const currentUser = useStore((s) => s.currentUser);
  const authChecked = useStore((s) => s.authChecked);
  const bootstrapAuth = useStore((s) => s.bootstrapAuth);

  useEffect(() => {
    if (!authChecked) {
      bootstrapAuth();
    }
  }, [authChecked, bootstrapAuth]);

  if (authChecked && currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MarketingShell pageClass="mk-landing">
      <section className="mk-hero mk-hero-split">
        <div className="mk-hero-copy mk-fade" style={{ animationDelay: "0ms" }}>
          <p className="sg-eyebrow">The trust layer for caring humanoids</p>
          <h1>
            We don&rsquo;t build the robot. We build
            <span className="mk-hero-accent"> the part you have to trust.</span>
          </h1>
          <p className="mk-hero-text">
            Manifold Kinetics is building the intelligence that plans
            what a robot should do, the safety envelope that can refuse
            unsafe requests, and the signed record of what the software
            requested and refused. The body is your partner&rsquo;s. The
            judgement layer is ours — and today, its safety mechanisms
            are validated in simulation, not yet on physical hardware.
          </p>
          <div className="sg-hero-actions">
            <Link
              to={PUBLIC_ROUTES.contact}
              className="sg-btn sg-btn-primary"
            >
              Request a pilot
            </Link>
            <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-secondary">
              How they&rsquo;re built
            </Link>
          </div>
          <ul className="mk-trust-strip" aria-label="Current product status">
            <li><strong>Simulation-tested</strong> safety envelope</li>
            <li><strong>Ed25519</strong> signed records</li>
            <li><strong>Explicit</strong> capability gaps</li>
            <li><strong>Hardware tests</strong> still pending</li>
            <li><strong>Local-first</strong> target architecture</li>
          </ul>
        </div>
        <div className="mk-hero-art mk-fade" style={{ animationDelay: "120ms" }}>
          <figure className="mk-art-frame">
            <div className="mk-art-bar" aria-hidden="true">
              <span>mk-vision // world-twin render</span>
              <span><em>reflex armed</em></span>
            </div>
            <img
              src="/ascii-hero.png"
              width="1500"
              height="1520"
              fetchPriority="high"
              alt="An imagined care robot standing night watch beside a child's bed under a moonlit window, rendered in ASCII characters"
            />
            <div className="mk-art-bar bottom" aria-hidden="true">
              <span><em>✓</em> ledger signed</span>
              <span>scan 0042 · a home</span>
              <span><strong>sim</strong> validation</span>
            </div>
          </figure>
        </div>
      </section>

      <section className="mk-principle mk-fade" style={{ animationDelay: "240ms" }} aria-labelledby="principle-title">
        <div className="mk-principle-split">
          <figure className="mk-art-frame">
            <div className="mk-art-bar" aria-hidden="true">
              <span>mk-vision // the relationship</span>
              <span><em>reflex armed</em></span>
            </div>
            <img
              src="/ascii-maternal.png"
              width="1500"
              height="1520"
              loading="lazy"
              decoding="async"
              alt="A care robot bending gently over a rocking cradle beneath a hanging mobile, rendered in ASCII characters: a stronger mind keeping watch over a weaker one"
            />
            <div className="mk-art-bar bottom" aria-hidden="true">
              <span><em>✓</em> refusals signed</span>
              <span>stronger watches</span>
              <span><strong>weaker decides</strong></span>
            </div>
          </figure>
          <div className="mk-principle-copy">
            <p className="sg-section-kicker">Maternal AI · the principle behind everything here</p>
            <h2 id="principle-title">
              A robot that is more capable than you should want you to thrive.
            </h2>
            <p>
              Geoffrey Hinton put it plainly: the only example we have
              of a more intelligent thing being steered by a less
              intelligent one is a mother and her baby, and she does
              not protect the child because she is told to. Nobody
              knows how to build that instinct yet. We build its
              habits, one mechanism at a time: a layer that notices
              first, refuses, steps back, stays when told to go away,
              and can be checked by the people it is there for.
            </p>
            <Link to={PUBLIC_ROUTES.maternal} className="sg-pillar-link">
              Why we call it Maternal AI →
            </Link>
          </div>
        </div>
      </section>

      <section className="mk-verticals mk-fade" style={{ animationDelay: "360ms" }} aria-labelledby="verticals-title">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">Where our intelligence is designed to work</p>
          <h2 id="verticals-title">Two settings, one trust layer.</h2>
        </div>
        <div className="mk-verticals-grid mk-verticals-grid-2">
          {VERTICALS.map((v) => (
            <Link
              to={v.link}
              className={`mk-vertical-card mk-tone-${v.tone}`}
              key={v.name}
            >
              <span className="mk-vertical-glyph" aria-hidden="true">{v.glyph}</span>
              <div className="mk-vertical-media">
                <img
                  src={v.img}
                  alt={v.imgAlt}
                  width="1500"
                  height="1520"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="mk-vertical-body">
                <p className="sg-section-kicker">{v.eyebrow} · {v.name}</p>
                <h3 dangerouslySetInnerHTML={{ __html: v.headline }} />
                <p dangerouslySetInnerHTML={{ __html: v.body }} />
                <span className="sg-pillar-link">{v.cta} →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mk-quote-band">
        <blockquote>
          &ldquo;The only model we have of a more intelligent thing
          being controlled by a less intelligent thing is a mother
          being controlled by her baby.&rdquo;
        </blockquote>
        <p className="mk-quote-attrib">— Geoffrey Hinton, Ai4, August 2025 · the idea every Manifold Kinetics mind is built around</p>
      </section>

      <section className="mk-promises">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What every one of them does</p>
          <h2>Four promises we will never compromise.</h2>
        </div>
        <div className="sg-pillars-grid grid-2">
          {PROMISES.map((p) => (
            <article className="sg-pillar" key={p.title}>
              <h3 dangerouslySetInnerHTML={{ __html: p.title }} />
              <p dangerouslySetInnerHTML={{ __html: p.body }} />
            </article>
          ))}
        </div>
      </section>

      <section className="mk-products" aria-labelledby="products-title">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What we ship</p>
          <h2 id="products-title">One trust layer, four disciplines.</h2>
        </div>
        <div className="mk-products-grid">
          {PRODUCTS.map((p) => (
            <Link to={p.link} className="mk-product-card" key={p.name}>
              <p className="sg-section-kicker">{p.pillar}</p>
              <h3>{p.name}</h3>
              <p>{p.body}</p>
              <span className="sg-pillar-link">Read the detail →</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mk-belief">
        <p className="sg-section-kicker">Why we exist</p>
        <h2>
          Humanoids should make people feel less alone — not more.
        </h2>
        <p>
          We&rsquo;re not interested in robots that replace human
          relationships. We&rsquo;re interested in the intelligence
          that lets a humanoid quietly do the work between visits —
          the noticing, the lifting, the gentle nudge that says
          &ldquo;maybe ring mum&rdquo;. We don&rsquo;t make the
          robots. We make the judgement that decides they&rsquo;re
          safe to be in the room.
        </p>
      </section>

      <section className="sg-final-cta">
        <p className="sg-section-kicker">Pilots open now</p>
        <h2>If you run a care provider or a production line — let&rsquo;s talk.</h2>
        <p>
          We&rsquo;re looking for design partners who will help test the
          system against real operating constraints. A pilot starts
          with a scoped workflow, explicit capability gaps, and a
          validation plan agreed before any hardware deployment.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.about} className="sg-btn sg-btn-secondary">
            Why we built this
          </Link>
          <Link to="/dashboard" className="sg-btn sg-btn-ghost">
            Open dashboard
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
